import React, { useEffect, useMemo, useState } from "react";
import { FaArrowLeft } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import CustomerSearchSelect from "../components/CustomerSearchSelect";
import "../assets/css/AddLead.css";
import "../assets/css/Issues.css";
import "../assets/css/Permissions.css";

function safeString(v) {
  return v == null ? "" : String(v);
}

function getCreatedBy() {
  try {
    const userStr = localStorage.getItem("user");
    if (!userStr) return 1;
    const user = JSON.parse(userStr);
    return user.user_id || user.id || 1;
  } catch {
    return 1;
  }
}

function getId(v) {
  if (v == null || v === "") return null;
  const n = parseInt(v, 10);
  return Number.isNaN(n) ? null : n;
}

function parseDecimal(v) {
  if (v == null || v === "") return null;
  const s = String(v).trim().replace(/,/g, ".");
  const n = parseFloat(s);
  return Number.isNaN(n) ? null : n;
}

const REQUIREMENT_TYPES = ["Rent", "Buy", "Lease"];

function AddRealestateLead({ onBack, onAddLead }) {
  const createdBy = getCreatedBy();

  const [formData, setFormData] = useState({
    clientId: "",
    propertyService: "",
    propertyServiceLink: "",
    firstName: "",
    lastName: "",
    email: "",
    mobileNumber: "",
    altPhone: "",
    requirementType: "",
    propertyTypeId: "",
    budgetMin: "",
    budgetMax: "",
    preferredLocation: "",
    propertyLocation: "",
    leadStatusId: "",
    sourceId: "",
    siteVisit: false,
    isOpportunity: false,
    automationEnabled: false,
    notes: "",
  });

  const [mobileCountryCode, setMobileCountryCode] = useState("+91");
  const [countries, setCountries] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [sources, setSources] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const phoneCountryOptions = useMemo(() => {
    return (countries || []).map((c) => ({
      id: c.country_id ?? c.Country_Id ?? c.id,
      code: (c.country_code || c.country_short_code || "").trim(),
      name: c.country_name || c.Country_Name || c.name || "",
    })).filter((x) => x.code);
  }, [countries]);

  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const [propertyRes, sourceRes, statusRes, countriesRes, customersRes] = await Promise.all([
          axiosInstance.get("/property-types").catch(() => ({ data: [] })),
          axiosInstance.get("/lead-sources"),
          axiosInstance.get("/lead-statuses"),
          axiosInstance.get("/countries").catch(() => ({ data: [] })),
          axiosInstance.get("/clients").catch(() => ({ data: [] })),
        ]);

        const rawCountries = Array.isArray(countriesRes?.data)
          ? countriesRes.data
          : Array.isArray(countriesRes?.data?.data)
            ? countriesRes.data.data
            : [];
        setCountries(rawCountries);

        const rawCustomers = Array.isArray(customersRes.data)
          ? customersRes.data
          : Array.isArray(customersRes.data?.results)
            ? customersRes.data.results
            : Array.isArray(customersRes.data?.data)
              ? customersRes.data.data
              : [];
        setCustomers(
          rawCustomers
            .map((item) => {
              const c = item.client || item;
              const id = c.client_id ?? c.client_id ?? c.id;
              const first = c.First_Name || c.first_name || "";
              const last = c.Last_Name || c.last_name || "";
              return {
                id,
                name: `${first} ${last}`.trim() || `Customer #${id}`,
                firstName: first,
                lastName: last,
                email: c.Email || c.email || "",
                phone: c.Phone || c.phone || "",
                countryCode: c.Country_Code || c.country_code || "+91",
                altPhone: c.Alt_Phone || c.alt_phone || "",
              };
            })
            .filter((c) => c.id != null)
        );

        const rawPropertyTypes = Array.isArray(propertyRes.data)
          ? propertyRes.data
          : Array.isArray(propertyRes.data?.data)
            ? propertyRes.data.data
            : [];
        setPropertyTypes(
          rawPropertyTypes
            .map((p) => ({
              id: p.Property_Type_Id ?? p.property_type_id ?? p.id,
              name: p.Property_Type_Name ?? p.property_type_name ?? p.name ?? "",
            }))
            .filter((p) => p.id != null && String(p.name).trim() !== "")
        );

        const rawSources = Array.isArray(sourceRes.data)
          ? sourceRes.data
          : Array.isArray(sourceRes.data?.data)
            ? sourceRes.data.data
            : [];
        setSources(
          rawSources
            .map((s) => ({
              id: s.Source_Id ?? s.source_id ?? s.id,
              name: s.Source_Name ?? s.source_name ?? s.name ?? "",
            }))
            .filter((s) => s.id != null && String(s.name).trim() !== "")
        );

        const rawStatuses = Array.isArray(statusRes.data)
          ? statusRes.data
          : Array.isArray(statusRes.data?.data)
            ? statusRes.data.data
            : [];
        setStatuses(
          rawStatuses
            .map((s) => ({
              id: s.Status_Id ?? s.status_id ?? s.id,
              name: s.Status ?? s.status ?? s.name ?? "",
            }))
            .filter((s) => s.id != null && String(s.name).trim() !== "")
        );
      } catch (err) {
        console.error("Error fetching real estate lead lookups:", err);
      }
    };
    fetchLookups();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue =
      name === "mobileNumber" || name === "altPhone"
        ? value.replace(/\D/g, "").slice(0, 10)
        : type === "checkbox"
          ? checked
          : value;
    setFormData((p) => ({ ...p, [name]: nextValue }));
  };

  const handleSelectClient = (clientId) => {
    const id = String(clientId || "");
    setFormData((p) => ({
      ...p,
      clientId: id,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const requiredFields = [
      ["clientId", formData.clientId],
      ["propertyService", formData.propertyService],
      ["propertyServiceLink", formData.propertyServiceLink],
      ["firstName", formData.firstName],
      ["mobileNumber", formData.mobileNumber],
      ["sourceId", formData.sourceId],
      ["leadStatusId", formData.leadStatusId],
    ];

    const missing = requiredFields
      .filter(([, v]) => !safeString(v).trim())
      .map(([k]) => k);
    if (missing.length) {
      alert("Please select a customer and fill all required lead fields.");
      return;
    }
    if (!/^\d{10}$/.test(formData.mobileNumber)) {
      alert("Mobile number must contain exactly 10 digits.");
      return;
    }
    if (formData.altPhone && !/^\d{10}$/.test(formData.altPhone)) {
      alert("Alternate phone must contain exactly 10 digits.");
      return;
    }

    const budgetMin = parseDecimal(formData.budgetMin);
    const budgetMax = parseDecimal(formData.budgetMax);
    if (formData.budgetMin && (budgetMin == null || budgetMin < 0)) {
      alert("Please enter a valid number for Budget Min.");
      return;
    }
    if (formData.budgetMax && (budgetMax == null || budgetMax < 0)) {
      alert("Please enter a valid number for Budget Max.");
      return;
    }
    if (budgetMin != null && budgetMax != null && budgetMin > budgetMax) {
      alert("Budget Min cannot be greater than Budget Max.");
      return;
    }

    const createdByNumber = getId(createdBy) ?? 1;

    const payload = {
      client_id: getId(formData.clientId),
      Property_Service: formData.propertyService.trim() || null,
      Property_Service_Link: formData.propertyServiceLink.trim() || null,
      First_Name: formData.firstName.trim(),
      Last_Name: formData.lastName.trim() || null,
      Email: formData.email.trim() || null,
      Country_Code: mobileCountryCode.trim() || "+91",
      Mobile_Number: formData.mobileNumber.trim(),
      Alt_Phone: formData.altPhone.trim() || null,
      Requirement_Type: formData.requirementType || null,
      Property_Type_Id: getId(formData.propertyTypeId),
      Budget_Min: budgetMin != null ? budgetMin : null,
      Budget_Max: budgetMax != null ? budgetMax : null,
      Preferred_Location: formData.preferredLocation.trim() || null,
      Property_Location: formData.propertyLocation.trim() || null,
      Lead_Status_Id: getId(formData.leadStatusId),
      Source_Id: getId(formData.sourceId),
      Site_Visit: !!formData.siteVisit,
      Is_Opportunity: !!formData.isOpportunity,
      Automation_Enabled: !!formData.automationEnabled,
      Notes: formData.notes.trim() || null,
      Created_By: createdByNumber,
    };

    setSubmitting(true);
    try {
      const response = await axiosInstance.post("/create-real-estate-lead", payload);
      const msg =
        response.data?.message ||
        response.data?.error ||
        "Real estate lead created successfully.";

      alert(msg);

      if (response.data && onAddLead) {
        onAddLead(response.data);
      }
      if (onBack) onBack();
    } catch (err) {
      const errorMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to create real estate lead.";
      console.error("Error creating real estate lead:", err);
      alert(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="addlead-container">
      <button type="button" className="back-link" onClick={onBack}>
        <FaArrowLeft /> Back
      </button>
      <div className="issues-main-card">
      <form className="addlead-form re-list-order-form" onSubmit={handleSubmit}>
        <div className="form-section">
          <div className="form-grid">
            <div className="form-group">
              <label>
                Client <span className="required">*</span>
              </label>
              <CustomerSearchSelect
                customers={customers}
                value={formData.clientId}
                onChange={handleSelectClient}
              />
            </div>

            <div className="form-group">
              <label>
                Property / Service <span className="required">*</span>
              </label>
              <input
                type="text"
                name="propertyService"
                value={formData.propertyService}
                onChange={handleChange}
                placeholder="Enter property or service"
                maxLength={255}
                required
              />
            </div>

            <div className="form-group">
              <label>Property Location</label>
              <input
                type="text"
                name="propertyLocation"
                value={formData.propertyLocation}
                onChange={handleChange}
                placeholder="Enter property location"
              />
            </div>

            <div className="form-group">
              <label>
                Property / Service Link <span className="required">*</span>
              </label>
              <input
                type="url"
                name="propertyServiceLink"
                value={formData.propertyServiceLink}
                onChange={handleChange}
                placeholder="https://example.com/property"
                maxLength={2048}
                required
              />
            </div>

          </div>
        </div>

        <div className="form-section">
          <div className="form-grid">
            <div className="form-group">
              <label>
                First Name <span className="required">*</span>
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>
                Mobile Number <span className="required">*</span>
              </label>
              <div style={{ display: "flex", gap: 8 }}>
                <select
                  value={mobileCountryCode}
                  onChange={(e) => setMobileCountryCode(e.target.value)}
                  style={{ maxWidth: 120 }}
                >
                  <option value="+91">+91</option>
                  {phoneCountryOptions.map((c) => (
                    <option key={c.id ?? c.code} value={c.code}>
                      {c.code}
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleChange}
                  inputMode="numeric"
                  maxLength={10}
                  pattern="[0-9]{10}"
                  title="Enter exactly 10 digits"
                  required
                  style={{ flex: 1 }}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Alt Phone</label>
              <input
                type="tel"
                name="altPhone"
                value={formData.altPhone}
                onChange={handleChange}
                inputMode="numeric"
                maxLength={10}
              />
            </div>

          </div>
        </div>

        <div className="form-section">
          <div className="form-grid">
            <div className="form-group">
              <label>Property Type</label>
              <select
                name="propertyTypeId"
                value={formData.propertyTypeId}
                onChange={handleChange}
              >
                <option value="">Select</option>
                {propertyTypes.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Budget Min</label>
              <input
                type="text"
                name="budgetMin"
                value={formData.budgetMin}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Budget Max</label>
              <input
                type="text"
                name="budgetMax"
                value={formData.budgetMax}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Requirement Type</label>
              <select
                name="requirementType"
                value={formData.requirementType}
                onChange={handleChange}
              >
                <option value="">Select</option>
                {REQUIREMENT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Lead Location</label>
              <input
                type="text"
                name="preferredLocation"
                value={formData.preferredLocation}
                onChange={handleChange}
              />
            </div>

          </div>
        </div>

        <div className="form-section">
          <div className="form-grid">
            <div className="form-group">
              <label>
                Source <span className="required">*</span>
              </label>
              <select
                name="sourceId"
                value={formData.sourceId}
                onChange={handleChange}
                required
              >
                <option value="">Select</option>
                {sources.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>
                Status <span className="required">*</span>
              </label>
              <select
                name="leadStatusId"
                value={formData.leadStatusId}
                onChange={handleChange}
                required
              >
                <option value="">Select</option>
                {statuses.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="siteVisit"
                  checked={formData.siteVisit}
                  onChange={handleChange}
                />
                <span>Site Visit</span>
              </label>
            </div>
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="isOpportunity"
                  checked={formData.isOpportunity}
                  onChange={handleChange}
                />
                <span>Opportunity</span>
              </label>
            </div>
            <div className="form-group checkbox-group">
              <label className="automation-switch">
                <input
                  type="checkbox"
                  name="automationEnabled"
                  checked={formData.automationEnabled}
                  onChange={handleChange}
                />
                <span className="automation-switch-track" aria-hidden="true">
                  <span className="automation-switch-thumb" />
                </span>
                <span>Automation</span>
              </label>
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="form-grid">
            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
              <label>Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-add-lead" disabled={submitting}>
            {submitting ? "Submitting..." : "Create Real Estate Enquiry"}
          </button>
        </div>
      </form>
      </div>
    </div>
  );
}

export default AddRealestateLead;
